import React, { useMemo, useEffect, useState } from 'react';
import { Tree } from 'antd';
import type { TreeDataNode, TreeProps } from 'antd';
import { Circle, Globe } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useLibrary } from '../../context/library';
import { getFolderIcon } from '../../constants/clients/folder-icons';
import { useClientId } from '../../hooks/use-client-id';
import { useResponsiveSizes } from '../../hooks/use-responsive-sizes';
import { getFolderName } from '../../utils';

const { DirectoryTree } = Tree;

const buildTreeFromFolders = (folders: string[], clientId: string): TreeDataNode[] => {
  const tree: TreeDataNode[] = [];
  const nodeMap: { [key: string]: TreeDataNode } = {};

  // PASO 1: Crear todos los nodos (incluyendo padres intermedios)
  folders.forEach(folderPath => {
    const pathParts = folderPath.split('/');

    for (let index = 0; index < pathParts.length; index++) {
      const currentPath = pathParts.slice(0, index + 1).join('/');

      if (!nodeMap[currentPath]) {
        const isRootLevel = index === 0;
        const label = getFolderName(currentPath);

        nodeMap[currentPath] = {
          title: label,
          key: currentPath,
          isLeaf: true,
          icon: (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              {isRootLevel ? getFolderIcon(currentPath, clientId) : <Circle size={10} fill='#514f51' strokeWidth={0} />}
            </div>
          ),
          children: []
        };
      }
    }
  });

  // PASO 2: Armar jerarquía - iterar sobre TODOS los nodos
  Object.keys(nodeMap).forEach(folderPath => {
    const pathParts = folderPath.split('/');

    if (pathParts.length === 1) {
      tree.push(nodeMap[folderPath]);
    } else {
      const parentPath = pathParts.slice(0, -1).join('/');
      nodeMap[parentPath].children!.push(nodeMap[folderPath]);
      nodeMap[parentPath].isLeaf = false;
    }
  });

  return tree;
};

// Función para obtener todos los padres de una carpeta
const getParentKeys = (folderPath: string): string[] => {
  const parts = folderPath.split('/');
  const parents: string[] = [];

  for (let i = 1; i < parts.length; i++) {
    parents.push(parts.slice(0, i).join('/'));
  }

  return parents;
};

interface TreeViewProps {
  darkMode?: boolean; // Para usar en el drawer móvil
}

export const TreeView: React.FC<TreeViewProps> = ({ darkMode = false }) => {
  const navigate = useNavigate();
  const { fontSize } = useResponsiveSizes();
  const { folders, currentFolder } = useLibrary();
  const clientId = useClientId();

  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);

  const selectedKeys = useMemo(() => {
    if (!currentFolder) return [];
    return [currentFolder];
  }, [currentFolder]);

  // Auto-expandir carpetas padres cuando cambia currentFolder
  useEffect(() => {
    if (currentFolder) {
      const parentKeys = getParentKeys(currentFolder);
      setExpandedKeys(prev => {
        const newKeys = new Set([...prev, ...parentKeys]);
        return Array.from(newKeys);
      });
    }
  }, [currentFolder]);

  const onSelect: TreeProps['onSelect'] = selectedKeys => {
    const folderPath = selectedKeys[0] as string;
    navigate(`/library/${encodeURIComponent(folderPath)}`);
  };

  const onExpand: TreeProps['onExpand'] = expandedKeys => {
    setExpandedKeys(expandedKeys as string[]);
  };

  const handleShowAll = () => {
    navigate('/library');
  };

  const treeData = buildTreeFromFolders(folders, clientId);

  return (
    <>
      {/* Estilos CSS para tema oscuro */}
      {darkMode && (
        <style>
          {`
            .tree-dark-mode .ant-tree {
              background: transparent !important;
              color: #d1d1d1 !important;
            }
            .tree-dark-mode .ant-tree-node-content-wrapper {
              color: #d1d1d1 !important;
            }
            .tree-dark-mode .ant-tree-node-content-wrapper:hover {
              background-color: rgba(255, 255, 255, 0.1) !important;
            }
            .tree-dark-mode .ant-tree-node-selected .ant-tree-node-content-wrapper {
              background-color: rgba(51, 63, 131, 0.5) !important;
            }
            .tree-dark-mode .ant-tree-switcher {
              color: #999 !important;
            }
            .tree-dark-mode .ant-tree-title {
              color: #d1d1d1 !important;
            }
          `}
        </style>
      )}

      <div
        style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
        className={darkMode ? 'tree-dark-mode' : ''}
      >
        <div
          onClick={handleShowAll}
          style={{
            padding: '12px 16px',
            backgroundColor: !currentFolder ? '#333F83' : darkMode ? 'rgba(255,255,255,0.1)' : '#f5f5f5',
            color: !currentFolder ? 'white' : darkMode ? '#d1d1d1' : '#666',
            borderRadius: '12px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontWeight: 500,
            transition: 'all 0.2s ease',
            border: `1px solid ${!currentFolder ? '#333F83' : darkMode ? 'rgba(255,255,255,0.2)' : '#e5e5e5'}`,
            fontSize: fontSize.base
          }}
        >
          <Globe size={16} />
          Todos los assets
        </div>

        <DirectoryTree
          multiple={false}
          showIcon={true}
          selectedKeys={selectedKeys}
          expandedKeys={expandedKeys}
          treeData={treeData}
          onSelect={onSelect}
          onExpand={onExpand}
          style={{
            width: '100%',
            textAlign: 'left',
            fontSize: fontSize.base,
            background: 'transparent'
          }}
          className='custom-tree-padding'
        />
      </div>
    </>
  );
};
